import { FC } from 'react';

import { useFormik } from 'formik';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { ExportTournamentResultsForm, initialValues, validationSchema } from './form';

import { tournamentsService } from 'v2/api/tournamentsService';
import { Switch } from 'v2/components/switch/Switch';
import { TournamentModel } from 'v2/types/TournamentModel';
import { handleHttpError } from 'v2/utils';

interface ExportTournamentResultsDialogProps {
  tournament: TournamentModel;
  isOpen: boolean;
  onClose: VoidFunction;
}

export const ExportTournamentResultsDialog: FC<ExportTournamentResultsDialogProps> = ({
  tournament,
  isOpen,
  onClose,
}) => {
  const { trigger, isMutating } = useSWRMutation(
    ['exportTournamentResults', tournament.id],
    tournamentsService.exportTournamentResults,
    {
      onSuccess: () => {
        handleClose();
      },
      onError: handleHttpError,
    }
  );

  const formik = useFormik<ExportTournamentResultsForm>({
    initialValues,
    validationSchema,
    onSubmit: ({ limit, onlyWinners }) => {
      if (isMutating) return;

      trigger({ onlyWinners, limit: limit === '' ? undefined : limit });
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const limitError = formik.touched.limit && formik.errors.limit;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Export Tournament Results</DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent className="form">
          <Switch
            name="onlyWinners"
            label="Only winners"
            checked={formik.values.onlyWinners}
            onChange={formik.handleChange}
          />

          <TextField
            name="limit"
            value={formik.values.limit}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Limit"
            type="number"
            error={!!limitError}
            helperText={limitError || (formik.values.onlyWinners ? 'Empty = all winners' : 'Empty = all participants')}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button type="submit" variant="contained" loading={isMutating} disabled={isMutating}>
            Export
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
